import { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import {
  UNLIMITED_PAGE_SCALE,
  UNLIMITED_PLAN_PAGE_BUDGET,
} from "~/helpers/consts";
import { MappedValidationError } from "~/utils/errors/validationErrors.ts";

import { SiteCreateAttributes } from "../../../api/Api";
import { Icon } from "../../../assets/icons/Icon";
import { URL_VALIDATION_REG_EX } from "../../../helpers/helpers";
import labels from "../../../helpers/labels";
import FormLabel from "../../login/formLabel/FormLabel";
import Select from "../../mantine/select/Select";
import Button from "../../ui/button/Button";
import Input from "../../ui/input/Input";
import RangeSlider from "../../ui/rangeSlider/RangeSlider";
import { tabs } from "./NewSite";
import styles from "./NewSiteDetails.module.scss";
import { PLATFORMS } from "./siteDetails/constants";

const fileTypes = ["CSV"];

const CROWL_MODE_WHITELISTED = "whitelisted";

type NewSiteDetailsProps = {
  form: ReturnType<typeof useForm<SiteCreateAttributes>>;
  setActiveTab: (tab: string) => void;
  serverErrors: MappedValidationError[];
  createSite: (data: SiteCreateAttributes) => void;
  newSiteId: number | undefined;
  mutateFile: (variables: {
    file: File;
    collection?: string;
    reflection?: string;
  }) => void;
  isPendingSite: boolean;
  /** undefined while loading; null when the plan has no page cap */
  pageUsageMax: number | null | undefined;
  hasVisibilityEngineEntitlement: boolean;
};

const NewSiteDetails = ({
  form,
  setActiveTab,
  serverErrors,
  createSite,
  newSiteId,
  mutateFile,
  isPendingSite,
  pageUsageMax,
  hasVisibilityEngineEntitlement,
}: NewSiteDetailsProps) => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    control,
    formState: { errors },
  } = form;

  const onSubmit = async (data: SiteCreateAttributes) => {
    if (!newSiteId) {
      const payload = {
        ...(data?.crawl_mode && { crawl_mode: CROWL_MODE_WHITELISTED }),
        url: data?.url,
        platform_cms: data.platform_cms,
        page_max_override: Number(data.page_max_override),
      };
      createSite(payload);
    }
    if (file) {
      mutateFile({
        file,
        collection: "page_imports",
        reflection: "input_file",
      });
    }
  };
  const handleChange = async (file: File) => {
    setFile(file);
    //@ts-expect-error fix when upload done
    setValue("page_imports_attributes", { input_file: file });
  };

  const { crawl_mode, url, page_imports_attributes, page_max_override } =
    getValues();
  watch("crawl_mode");
  watch("url");
  watch("page_max_override");
  const isCrawlMode = !(!crawl_mode || (crawl_mode && page_imports_attributes));
  const resetNewSiteId = () => {
    localStorage.removeItem("siteId");
    navigate("/");
  };
  const isUnlimitedPages = pageUsageMax === null;

  useEffect(() => {
    if (pageUsageMax !== undefined) {
      form.reset({
        ...form.getValues(),
        page_max_override: pageUsageMax ?? UNLIMITED_PLAN_PAGE_BUDGET,
      });
    }
  }, [pageUsageMax]);

  return (
    <>
      {/*
       // @ts-expect-error TS2339 non-existing className */}
      <div className={styles.container}>
        <div className={styles.content}>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.formSection}>
            <h2 className={styles.title}>Add Your Site</h2>
            <p className={styles.description}>
              Enter your website URL. We will run a full scan to find holes in
              AI accessibility as well as technical on-page optimization
              opportunities.
            </p>
            <form onSubmit={handleSubmit(onSubmit)} id="newSiteDetailsForm">
              <div className={styles.inputGroup}>
                <FormLabel label="URL" />
                <Input
                  disabled={!!newSiteId}
                  placeholder="example.com"
                  errormessage={
                    errors.url
                      ? (errors.url.message as string) || labels.INVALID_URL
                      : ""
                  }
                  {...register("url", {
                    required: true,
                    pattern: {
                      value: URL_VALIDATION_REG_EX,
                      message: labels.INVALID_URL,
                    },
                  })}
                />
              </div>
              {hasVisibilityEngineEntitlement && (
                <div className={styles.inputGroup}>
                  <Controller
                    name="platform_cms"
                    control={control}
                    render={({ field }) => (
                      <Select
                        label={<FormLabel label="Website Platform" />}
                        data={PLATFORMS}
                        searchable
                        allowDeselect={false}
                        disabled={!!newSiteId}
                        {...field}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                      />
                    )}
                  />
                </div>
              )}
              <div className={styles.titleWithIcon}>
                <FormLabel label="Pages to Crawl" />
                <>
                  <a data-tooltip-id="pageUsageMax">
                    <Icon iconName="InfoIcon" width="1rem" height="1rem" />
                  </a>
                  <Tooltip
                    style={{ borderRadius: "var(--border-radius)" }}
                    id="pageUsageMax"
                    content={
                      isUnlimitedPages
                        ? `Current limit: ${(page_max_override ?? UNLIMITED_PLAN_PAGE_BUDGET).toLocaleString()} pages for this site. Your plan has no page cap — pages are billed as they are crawled.`
                        : `Current limit: ${page_max_override === pageUsageMax ? "Unlimited" : page_max_override?.toLocaleString()} pages. There are ${(pageUsageMax ?? 0).toLocaleString()} pages included in your plan`
                    }
                    place="top"
                  />
                </>
              </div>
              <div className={styles.rangeSlider}>
                <RangeSlider
                  editableInput={true}
                  disabled={!!newSiteId}
                  defaultValue={page_max_override}
                  value={page_max_override}
                  valueFormat="comma"
                  onChange={(value) =>
                    setValue("page_max_override", value, {
                      shouldDirty: true,
                    })
                  }
                  max={isUnlimitedPages ? undefined : (pageUsageMax ?? 0)}
                  scaleStops={
                    isUnlimitedPages ? UNLIMITED_PAGE_SCALE : undefined
                  }
                />
              </div>
              <div className={styles.checkboxGroup}>
                <input
                  disabled={!!newSiteId}
                  type="checkbox"
                  id="crawlFromList"
                  onClick={() =>
                    !crawl_mode &&
                    setValue("page_imports_attributes", undefined)
                  }
                  {...register("crawl_mode")}
                />
                <label htmlFor="crawlFromList">
                  Crawl Pages from a White List
                  <span className={styles.checkboxGroupHint}>
                    Upload a white list of select pages to crawl and optimize.
                    Other pages will be excluded.
                  </span>
                </label>
              </div>
              {crawl_mode && !newSiteId && (
                <div className={styles.fileUploader}>
                  <FileUploader
                    disabled={!!newSiteId}
                    uploadedLabel={file?.name}
                    handleChange={handleChange}
                    name="file"
                    types={fileTypes}
                    label="Upload or drag and drop a CSV file. CSV file should NOT contain headers, only a single column containing page URLs."
                  />
                </div>
              )}
            </form>
          </div>
        </div>
        <div className={styles.footer}>
          <Button outlined onClick={resetNewSiteId}>
            {newSiteId ? "Back" : "Cancel"}
          </Button>
          {!newSiteId ? (
            <Button
              withSpinner={isPendingSite}
              type="submit"
              formId="newSiteDetailsForm"
              disabled={
                isCrawlMode ||
                !url ||
                !!errors.url ||
                isPendingSite ||
                serverErrors.length > 0
              }
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={() => {
                setActiveTab(tabs[2]);
                navigate(tabs[2]);
              }}
            >
              Continue
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
export default NewSiteDetails;
